import React, { useContext, useState, useEffect } from "react";
import { Modal, Button, TextInput, Label } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import Select from "react-select";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";
import Head_data from "../components/Head_data";

export const VendorAdvance = ({ theme }) => {
    const { globalValue } = useContext(GlobalContext);

    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("add");

    const [selectedVendor, setSelectedVendor] = useState(null);
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    });

    // NEW STATES FOR AUTO-DUPLICATE APPEND LOGIC ONLY
    const [tempOldAmount, setTempOldAmount] = useState(0);
    const [autoAppendMode, setAutoAppendMode] = useState(false);

    const [editId, setEditId] = useState(null);
    const [hasExistingAdvance, setHasExistingAdvance] = useState(false);
    const [existingAdvanceData, setExistingAdvanceData] = useState(null);

    /* ------- STATES FOR LOG VIEW -------- */
    const [logOpen, setLogOpen] = useState(false);
    const [logData, setLogData] = useState([]);
    const [logVendor, setLogVendor] = useState("");

    /* ---------------- FETCH DATA ---------------- */

    const fetchVendors = async () => {
        try {
            const response = await axios.get(`${globalValue}/vendor`, {
                params: {
                    data: sessionStorage.getItem("branch"),
                    position: "",
                },
                headers: {
                    "x-access-token": sessionStorage.getItem("token"),
                },
            });
            setVendors(response.data || []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
        }
    };

    const fetchVendorAdvance = async () => {
        try {
            const branch = sessionStorage.getItem("branch");

            const res = await axios.get(
                `${globalValue}/vendor_advance_display`,
                {
                    params: {
                        data: branch
                    }
                }
            );

            setAdvances(res.data || []);
        } catch (error) {
            console.error("Error fetching vendor advances:", error);
        }
    };

    useEffect(() => {
        fetchVendors();
        fetchVendorAdvance();
    }, []);

    // AUTO-DUPLICATE DETECTION - ONLY in ADD mode
    useEffect(() => {
        if (selectedVendor && advances.length > 0 && mode === "add") {
            // ✅ FIXED: Use object properties instead of array indices
            const vendorData = vendors.find(v => v.id === selectedVendor.value);
            const vendorId = vendorData ? vendorData.id : null;
            
            const existing = advances.find(advance => 
                advance.vendor_id === selectedVendor.value ||
                advance.vendor_code === selectedVendor.value
            );
            
            if (existing) {
                setHasExistingAdvance(true);
                setExistingAdvanceData(existing);
                setEditId(existing.id);
                
                // AUTO-APPEND: Store old amount, reset input to 0
                setTempOldAmount(parseFloat(existing.amount) || 0);
                setAmount("0");
                setDate(new Date(existing.date).toISOString().split("T")[0]);
                setAutoAppendMode(true);
            } else {
                setHasExistingAdvance(false);
                setExistingAdvanceData(null);
                setEditId(null);
                setTempOldAmount(0);
                setAutoAppendMode(false);
                setAmount("");
            }
        }
    }, [selectedVendor, advances, vendors, mode]);

    const fetchAdvanceLog = async (advance_id, vendorName, vendor_code) => {
        try {
            const response = await axios.get(`${globalValue}/vendor_advance_log_display`, {
                params: { advance_id }
            });
            setLogData(response.data.data || []);
            setLogVendor(vendorName + " (" + vendor_code + ")");
            setLogOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const filteredData = advances.filter((row) =>
        row.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.vendor_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.mobile_no?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ FIXED: Correct mapping for object structure from API
    const vendorOptions = vendors.map((vendor) => ({
        value: vendor.id,                           // Use 'id' as value
        label: `${vendor.vendor_name} (${vendor.mobile_no || 'N/A'})`, // vendor_name + mobile_no
    }));

    const handleOpenAdd = () => {
        setMode("add");
        setSelectedVendor(null);
        setAmount("");
        setDate(new Date().toISOString().split("T")[0]);
        setEditId(null);
        setHasExistingAdvance(false);
        setExistingAdvanceData(null);
        setTempOldAmount(0);
        setAutoAppendMode(false);
        setOpen(true);
    };

    // ✅ FIXED: Updated for vendor object structure
    const handleEditVendor = (row) => {
        setMode("edit");
        setEditId(row.id);
        setSelectedVendor({
            value: row.vendor_id || row.id,
            label: `${row.vendor_name || row.customer_name} (${row.mobile_no || row.vendor_code || row.vehicle_no})`,
        });
        setAmount(row.amount);
        setDate(new Date(row.date).toISOString().split("T")[0]);
        setHasExistingAdvance(false);
        setAutoAppendMode(false);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete?",
            text: "Are you sure?",
            icon: "warning",
            showCancelButton: true,
        });

        if (!confirm.isConfirmed) return;

        try {
            await axios.post(`${globalValue}/vendor_advance_delete`, { id });
            Swal.fire("Deleted!");
            fetchVendorAdvance();
        } catch (error) {
            Swal.fire("Error!", "Failed to delete.", "error");
        }
    };

    const handleSubmit = async () => {
        if (!selectedVendor || !amount || !date) {
            Swal.fire({
                icon: "warning",
                title: "Missing Fields",
                text: "All fields are required!",
            });
            return;
        }

        const enteredAmount = parseFloat(amount) || 0;
        let finalAmount = enteredAmount;
        let payload;
        let apiEndpoint;
        let successMessage;

        // 1. AUTO-APPEND MODE (duplicate detection in ADD mode)
        if (autoAppendMode) {
            finalAmount = tempOldAmount + enteredAmount;
            payload = {
                vendor_id: selectedVendor.value,
                amount: finalAmount.toString(),
                date,
                id: editId
            };
            apiEndpoint = `${globalValue}/vendor_advance_edit`;
            successMessage = `Vendor Advance Updated! New Total: ₹${finalAmount.toLocaleString()}`;
        } 
        // 2. NORMAL ADD MODE
        else if (mode === "add") {
            payload = {
                vendor_id: selectedVendor.value,
                amount: enteredAmount.toString(),
                date,
                branch: sessionStorage.getItem("branch")
            };
            apiEndpoint = `${globalValue}/vendor_advance_add`;
            successMessage = "Vendor Advance Added!";
        } 
        // 3. NORMAL EDIT MODE (direct replace)
        else {
            payload = {
                vendor_id: selectedVendor.value,
                amount: enteredAmount.toString(),
                date,
                id: editId
            };
            apiEndpoint = `${globalValue}/vendor_advance_edit`;
            successMessage = "Vendor Advance Updated!";
        }

        try {
            setLoading(true);
            const response = await axios.post(apiEndpoint, payload);

            Swal.fire({
                icon: "success",
                title: successMessage,
                timer: 2000,
                showConfirmButton: false,
            });

            setOpen(false);
            fetchVendorAdvance();

        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Something went wrong!",
            });
        } finally {
            setLoading(false);
        }
    };

    const customStyles = {
        table: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                width: "100%",
                border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(226, 232, 240)",
            },
        },
        headCells: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                color: theme === "dark" ? "white" : "black",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                color: theme === "dark" ? "white" : "black",
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(255, 255, 255)",
            },
        },
        pagination: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                borderTop: theme === "dark" ? "solid 1px white" : "solid 1px rgb(226, 232, 240)",
            },
            pageButtonsStyle: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                fill: theme === "dark" ? "white" : "black",
                '&:hover': {
                    backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                },
            },
        },
    };

    const columns = [
        {
            name: "S.No",
            selector: (row) => row.sno,
            sortable: false,
            width: "80px",
        },
        {
            name: "Vendor",
            selector: (row) => `${row.vendor_name || row.customer_name} (${row.vendor_code || row.vehicle_no || row.mobile_no})`,
            sortable: true,
        },
        {
            name: "Mobile",
            selector: (row) => row.mobile_no,
        },
        {
            name: "Amount",
            selector: (row) => `₹${parseFloat(row.amount).toLocaleString()}`,
        },
        {
            name: "Date",
            selector: (row) => {
                const d = new Date(row.date);
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                return `${day}-${month}-${year}`;
            },
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex gap-3">
                    <button
                        onClick={() => handleEditVendor(row)}
                        className="p-2 text-white bg-blue-700 hover:bg-blue-800 rounded-md shadow-xl"
                        title="Edit"
                    >
                        <i className="fa fa-edit"></i>
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="p-2 text-white bg-yellow-500 hover:bg-yellow-700 rounded-md shadow-xl"
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                    {/* <button
                        onClick={() =>
                            fetchAdvanceLog(row.id, row.vendor_name || row.customer_name, row.vendor_code || row.mobile_no || row.vehicle_no)
                        }
                        className="p-2 text-white bg-green-600 hover:bg-green-700 rounded-md shadow-xl"
                        title="View Log"
                    >
                        <i className="fa fa-eye"></i>
                    </button> */}
                </div>
            ),
        },
    ];

    return (
        <div
            className={`md:ml-[250px] mt-16 h-[92vh] flex justify-center items-center ${
                theme === "dark"
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-800"
            }`}
        >
            {loading && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
                    <img src={engineGif} alt="Loading..." className="w-24 h-24" />
                </div>
            )}

            <div className="w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl bg-white overflow-auto">
                <div className="w-full flex justify-between text-gray-600">
                    <Head_data data="Manage Vendor Advance" size="3xl" color={theme === "dark" ? "text-gray-200" : "text-gray-800"} />
                    <button
                        onClick={handleOpenAdd}
                        className="px-4 py-2 mt-4 text-white bg-blue-600 rounded hover:bg-blue-700 md:mt-2"
                    >
                        Add Vendor Advance
                    </button>
                </div>

                <input
                    type="text"
                    placeholder="Search vendor, code, mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full md:w-1/3 px-4 my-2 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none"
                />

                <DataTable
                    columns={columns}
                    data={filteredData.map((row, idx) => ({ ...row, sno: idx + 1 }))}
                    highlightOnHover
                    pagination
                    customStyles={customStyles}
                />
            </div>

            <Modal show={open} onClose={() => setOpen(false)}>
                <Modal.Header>
                    {autoAppendMode ? "Update Vendor Advance (Append Amount)" : 
                     (mode === "add" ? "Add Vendor Advance" : "Edit Vendor Advance")}
                </Modal.Header>
                <Modal.Body>
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <Label value="Vendor" />
                            <Select
                                options={vendorOptions}
                                value={selectedVendor}
                                onChange={setSelectedVendor}
                                placeholder="Select vendor"
                                isDisabled={mode === "edit"}
                                classNamePrefix={autoAppendMode ? "border-yellow-500" : ""}
                            />
                            {autoAppendMode && (
                                <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <p className="text-yellow-800 text-sm">
                                        <strong>Old Amount:</strong> ₹{tempOldAmount.toLocaleString()} 
                                        | <strong>New Total:</strong> ₹{(tempOldAmount + (parseFloat(amount) || 0)).toLocaleString()}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div>
                            <Label value={autoAppendMode ? "New Amount to Add" : "Amount"} />
                            <TextInput
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder={autoAppendMode ? "Enter amount to ADD to existing" : "Enter amount"}
                                className={autoAppendMode ? "border-yellow-500" : ""}
                            />
                        </div>

                        <div>
                            <Label value="Date" />
                            <TextInput
                                type="date"
                                value={date}
                                disabled={sessionStorage.getItem("position") !== "superadmin"}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        color={autoAppendMode ? "warning" : "blue"}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Processing..." : 
                         (autoAppendMode ? `Update (Total: ₹${(tempOldAmount + (parseFloat(amount) || 0)).toLocaleString()})` : 
                          (mode === "add" ? "Add Vendor Advance" : "Update"))}
                    </Button>
                    <Button 
                        color="gray" 
                        onClick={() => setOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={logOpen} onClose={() => setLogOpen(false)} size="6xl">
                <Modal.Header>Vendor Advance Log – {logVendor}</Modal.Header>
                <Modal.Body>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-300">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="border px-3 py-2">Date</th>
                                    <th className="border px-3 py-2">Amount</th>
                                    <th className="border px-3 py-2">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logData.length > 0 ? (
                                    logData.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-100">
                                            <td className="border px-3 py-2 text-center">
                                                {new Date(item.date).toISOString().split("T")[0]}
                                            </td>
                                            <td className="border px-3 py-2 text-center">{item.amount}</td>
                                            <td className="border px-3 py-2 text-center">{item.description}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center p-3 text-gray-500">
                                            No logs found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button color="gray" onClick={() => setLogOpen(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
